import type { Deal, SalesState } from '@/types/deals';

// ============================================
// STATE TRANSITION RULES
// ============================================

const ALLOWED_TRANSITIONS: Record<SalesState, SalesState[]> = {
  1: [2, 5, 6], // Initial Contact → Evaluation, Parked, or Lost
  2: [3, 5, 6], // Evaluation → Verbal, Parked, or Lost
  3: [4, 5, 6], // Verbal → Closed, Parked, or Lost
  4: [],        // Closed/In Delivery (terminal)
  5: [1, 2, 3, 6], // Parked → Resume (any active state) or Lost
  6: [],        // Closed/Lost (terminal)
};

export function canTransition(from: SalesState, to: SalesState): boolean {
  return ALLOWED_TRANSITIONS[from].includes(to);
}

// Check hard gate conditions for State 3→4 transition
export function canTransitionToState4(deal: Deal): { allowed: boolean; reason?: string } {
  const hasPaymentFlag = deal.flags.some(f => f.type === 'payment' && f.active);
  const hasIntakeFlag = deal.flags.some(f => f.type === 'intake' && f.active);
  
  if (hasPaymentFlag) {
    return { allowed: false, reason: 'Payment Flag must be inactive (payment received)' };
  }
  if (hasIntakeFlag) {
    return { allowed: false, reason: 'Intake Flag must be inactive (intake complete)' };
  }
  return { allowed: true };
}

// Validate transition with hard gate enforcement
export function validateTransition(
  from: SalesState, 
  to: SalesState, 
  deal: Deal
): { valid: boolean; hardBlocked: boolean; warning?: string; reason?: string } {
  const isValidTransition = canTransition(from, to);
  
  // Hard gate: State 3 → State 4
  if (from === 3 && to === 4) {
    const gateCheck = canTransitionToState4(deal);
    if (!gateCheck.allowed) {
      return {
        valid: false,
        hardBlocked: true,
        reason: gateCheck.reason
      };
    }
  }
  
  // Soft guidance: warn but allow for other invalid transitions
  if (!isValidTransition) {
    return {
      valid: false,
      hardBlocked: false,
      warning: `Unusual transition from State ${from} to State ${to}. Consider adding exception flag if intentional.`
    };
  }
  
  return { valid: true, hardBlocked: false };
}

// ============================================
// ACTION RULES
// ============================================

// Allowed actions per state
const ALLOWED_ACTIONS: Record<SalesState, string[]> = {
  1: ['clarify_scope', 'identify_mode', 'qualify_authority'],
  2: ['justification_packets', 'roi_framing', 'clarifying_questions'],
  3: ['payment_instructions', 'timeline_contingent'],
  4: ['product_delivery_handoff', 'execution_coordination'],
  5: ['scheduled_checkins', 'hold_posture'],
  6: ['archive', 'record_reason'],
};

// Forbidden actions per state
const FORBIDDEN_ACTIONS: Record<SalesState, string[]> = {
  1: ['quote_pricing', 'engage_product_delivery', 'promise_timelines'],
  2: ['discount', 'pressure', 're_sell_fundamentals'],
  3: ['advance_scheduling', 'engage_product_delivery', 'customize_scope'],
  4: ['renegotiate_scope', 'revisit_pricing'],
  5: ['chase', 'pressure', 're_justify_repeatedly'],
  6: ['re_engage_without_trigger'],
};

export function canPerformAction(deal: Deal, action: string): boolean {
  const allowed = ALLOWED_ACTIONS[deal.sales_state];
  const forbidden = FORBIDDEN_ACTIONS[deal.sales_state];
  return allowed.includes(action) && !forbidden.includes(action);
}

// Check if Product / Delivery Authority is eligible (hard gate)
export function isProductDeliveryEligible(deal: Deal): boolean {
  if (deal.sales_state !== 4) return false;
  const hasPaymentFlag = deal.flags.some(f => f.type === 'payment' && f.active);
  const hasIntakeFlag = deal.flags.some(f => f.type === 'intake' && f.active);
  return !hasPaymentFlag && !hasIntakeFlag;
}

// ============================================
// DERIVED FIELDS
// ============================================

export function computeDerivedFields(deal: Deal): {
  next_suggested_action?: string;
  execution_eligible: boolean;
  product_delivery_eligible: boolean;
} {
  const executionEligible = isProductDeliveryEligible(deal);
  
  // Compute next suggested action based on state and flags
  let nextSuggestedAction: string | undefined;
  
  switch (deal.sales_state) {
    case 1:
      nextSuggestedAction = 'Clarify scope and identify Mode';
      break;
    case 2:
      if (deal.flags.some(f => f.type === 'payment' && f.active)) {
        nextSuggestedAction = 'Resolve payment before proceeding';
      } else {
        nextSuggestedAction = 'Send justification packets and frame ROI';
      }
      break;
    case 3:
      if (deal.flags.some(f => f.type === 'payment' && f.active)) {
        nextSuggestedAction = 'Await payment receipt';
      } else {
        nextSuggestedAction = 'Prepare for handoff to Product / Delivery';
      }
      break;
    case 4:
      nextSuggestedAction = 'Coordinate execution with Product / Delivery';
      break;
    case 5:
      nextSuggestedAction = 'Schedule check-in and maintain relationship';
      break;
    case 6:
      nextSuggestedAction = 'Archive and record reason';
      break;
  }
  
  return {
    next_suggested_action: nextSuggestedAction,
    execution_eligible: executionEligible,
    product_delivery_eligible: executionEligible,
  };
}
